/**
 * NewReferralModal — Pre-Live Polish PR-3 / P-7 (Feb 2026)
 * ---------------------------------------------------------------------------
 * Internal staff/admin form to capture a referral that arrived via phone,
 * email, walk-in, or internal conversation.
 *
 * Calls the EXISTING backend route:
 *   POST /api/referrals  (RBAC: referrals.manage)
 *
 * Backend handles:
 *   - referralNo auto-generation (REF-XXXXXX)
 *   - status='NEW'
 *   - referrerType='INTERNAL'
 *   - referrerUserId from JWT
 *   - stateId / branchId default from current_user
 *   - audit event: referrals.create
 *
 * v1 deliberately omits stateId / branchId selectors (no UI elsewhere exposes
 * them today; server defaulting keeps parity with the enquiry-to-referral flow).
 *
 * Required fields:  participantName, fundingType
 * Optional fields:  dateOfBirth, phone, email, urgencyLevel,
 *                   locationPreference, supportNeeds, notes
 */
import React, { useState } from 'react';
import api, { getErrorMessage } from '@/lib/api';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

const FUNDING_OPTIONS = [
  { value: 'NDIS',    label: 'NDIS' },
  { value: 'PRIVATE', label: 'Private' },
];
// NOTE (PR-3, Feb 2026): backend `CreateReferralRequest.fundingType` is
// `Literal['NDIS','PRIVATE']`. Extending to HOME_CARE / OTHER would require a
// separate backend change — out of scope for this PR.

const URGENCY_OPTIONS = [
  { value: 'LOW',    label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH',   label: 'High' },
  { value: 'URGENT', label: 'Urgent' },
];

const INITIAL_FORM = {
  participantName: '',
  fundingType: '',           // required — no default to force a conscious pick
  dateOfBirth: '',
  phone: '',
  email: '',
  urgencyLevel: 'MEDIUM',    // sensible default; user can override
  locationPreference: '',
  supportNeeds: '',
  notes: '',
};

function isValidEmail(s) {
  if (!s) return true; // optional → empty is fine
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

export function NewReferralModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState({});

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const markTouched = (k) => setTouched((t) => ({ ...t, [k]: true }));

  // Required-field validity for enabling the submit button.
  const nameValid = form.participantName.trim().length >= 2;
  const fundingValid = !!form.fundingType;
  const emailValid = isValidEmail(form.email.trim());
  const canSubmit = nameValid && fundingValid && emailValid && !submitting;

  const resetAndClose = () => {
    setForm(INITIAL_FORM);
    setTouched({});
    setSubmitting(false);
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setSubmitting(true);
    // Build payload — drop empty optional strings so the backend's defaults
    // can take over cleanly.
    const payload = { participantName: form.participantName.trim(), fundingType: form.fundingType };
    const optional = ['dateOfBirth', 'phone', 'email', 'urgencyLevel', 'locationPreference', 'supportNeeds', 'notes'];
    for (const k of optional) {
      const v = (form[k] ?? '').toString().trim();
      if (v) payload[k] = v;
    }
    try {
      const res = await api.post('/referrals', payload);
      const created = res.data?.data || res.data;
      toast.success(`Referral ${created?.referralNo || 'created'}.`);
      onCreated?.(created);
      resetAndClose();
    } catch (err) {
      const code = err?.response?.data?.error?.code;
      if (err?.response?.status === 403 || code === 'PERMISSION_DENIED') {
        toast.error('You no longer have permission to create referrals.');
        resetAndClose();
      } else {
        toast.error(getErrorMessage(err) || 'Could not create referral. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => { if (!o && !submitting) resetAndClose(); }}
    >
      <DialogContent className="max-w-lg" data-testid="new-referral-modal">
        <DialogHeader>
          <DialogTitle>New Referral</DialogTitle>
          <DialogDescription>
            Capture a referral received by phone, email, walk-in or internal conversation.
            Required fields are marked with *.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="new-referral-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-1 space-y-1">
              <Label htmlFor="ref-name">Participant name *</Label>
              <Input
                id="ref-name"
                value={form.participantName}
                onChange={(e) => set('participantName', e.target.value)}
                onBlur={() => markTouched('participantName')}
                disabled={submitting}
                data-testid="new-referral-field-participant-name"
                aria-invalid={touched.participantName && !nameValid}
                autoFocus
              />
              {touched.participantName && !nameValid && (
                <p className="text-xs text-red-600">Please enter the participant's name.</p>
              )}
            </div>

            <div className="sm:col-span-1 space-y-1">
              <Label htmlFor="ref-funding">Funding type *</Label>
              <Select
                value={form.fundingType}
                onValueChange={(v) => { set('fundingType', v); markTouched('fundingType'); }}
                disabled={submitting}
              >
                <SelectTrigger id="ref-funding" data-testid="new-referral-field-funding-type">
                  <SelectValue placeholder="Select funding…" />
                </SelectTrigger>
                <SelectContent>
                  {FUNDING_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {touched.fundingType && !fundingValid && (
                <p className="text-xs text-red-600">Please pick a funding type.</p>
              )}
            </div>

            <div className="space-y-1">
              <Label htmlFor="ref-dob">Date of birth</Label>
              <Input
                id="ref-dob"
                type="date"
                value={form.dateOfBirth}
                onChange={(e) => set('dateOfBirth', e.target.value)}
                disabled={submitting}
                data-testid="new-referral-field-dob"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="ref-urgency">Urgency</Label>
              <Select
                value={form.urgencyLevel}
                onValueChange={(v) => set('urgencyLevel', v)}
                disabled={submitting}
              >
                <SelectTrigger id="ref-urgency" data-testid="new-referral-field-urgency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {URGENCY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="ref-phone">Phone</Label>
              <Input
                id="ref-phone"
                value={form.phone}
                onChange={(e) => set('phone', e.target.value)}
                disabled={submitting}
                data-testid="new-referral-field-phone"
                placeholder="e.g. +61 4xx xxx xxx"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="ref-email">Email</Label>
              <Input
                id="ref-email"
                type="email"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                onBlur={() => markTouched('email')}
                disabled={submitting}
                data-testid="new-referral-field-email"
                aria-invalid={touched.email && !emailValid}
              />
              {touched.email && !emailValid && (
                <p className="text-xs text-red-600">Please enter a valid email address.</p>
              )}
            </div>

            <div className="sm:col-span-2 space-y-1">
              <Label htmlFor="ref-location">Location preference</Label>
              <Input
                id="ref-location"
                value={form.locationPreference}
                onChange={(e) => set('locationPreference', e.target.value)}
                disabled={submitting}
                data-testid="new-referral-field-location"
                placeholder="Suburb, postcode, or service area"
              />
            </div>

            <div className="sm:col-span-2 space-y-1">
              <Label htmlFor="ref-needs">Support needs</Label>
              <Textarea
                id="ref-needs"
                value={form.supportNeeds}
                onChange={(e) => set('supportNeeds', e.target.value)}
                disabled={submitting}
                data-testid="new-referral-field-support-needs"
                rows={3}
                placeholder="Brief summary of supports requested…"
              />
            </div>

            <div className="sm:col-span-2 space-y-1">
              <Label htmlFor="ref-notes">Notes</Label>
              <Textarea
                id="ref-notes"
                value={form.notes}
                onChange={(e) => set('notes', e.target.value)}
                disabled={submitting}
                data-testid="new-referral-field-notes"
                rows={2}
                placeholder="Any additional context for the triage team…"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={resetAndClose}
              disabled={submitting}
              data-testid="new-referral-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!canSubmit}
              data-testid="new-referral-submit"
            >
              {submitting ? 'Creating…' : 'Create Referral'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default NewReferralModal;
